import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaArrowRight, FaSearch, FaTimes, FaRegHourglass } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../utils/api";
import "./Webinars.css";

export default function Webinars() {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, upcoming, completed
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const fetchWebinars = async () => {
      setLoading(true);
      try {
        const res = await api.get("/webinars");
        setWebinars(res.data.data || []);
      } catch (err) {
        setError("Failed to load webinars. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchWebinars();
  }, []);

  // Find the next upcoming webinar for the countdown hero feature
  const upcomingWebinars = webinars.filter(w => new Date(w.starts_at) > new Date());
  const nextWebinar = upcomingWebinars.length > 0
    ? upcomingWebinars.reduce((closest, current) => {
        return new Date(current.starts_at) < new Date(closest.starts_at) ? current : closest;
      })
    : null;

  // Countdown timer effect
  useEffect(() => {
    if (!nextWebinar) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const difference = new Date(nextWebinar.starts_at) - new Date();
      if (difference <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [nextWebinar, webinars]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getWebinarStatus = (webinar) => {
    const webinarTime = new Date(webinar.starts_at).getTime();
    const durationMs = webinar.duration_minutes * 60 * 1000;
    const now = new Date().getTime();

    if (webinarTime <= now && now <= (webinarTime + durationMs)) {
      return { label: "LIVE NOW", class: "status-live" };
    } else if (webinarTime > now) {
      return { label: "UPCOMING", class: "status-upcoming" };
    } else {
      return { label: "COMPLETED", class: "status-completed" };
    }
  };

  // Filtering webinars based on search and selected tab
  const filteredWebinars = webinars.filter(webinar => {
    const matchesSearch = webinar.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (webinar.description && webinar.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const webinarTime = new Date(webinar.starts_at).getTime();
    const durationMs = webinar.duration_minutes * 60 * 1000;
    const now = new Date().getTime();

    const isUpcoming = webinarTime > now;
    const isLive = webinarTime <= now && now <= (webinarTime + durationMs);
    const isCompleted = now > (webinarTime + durationMs);

    if (filterStatus === "upcoming") {
      return matchesSearch && (isUpcoming || isLive);
    } else if (filterStatus === "completed") {
      return matchesSearch && isCompleted;
    }
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="webinars-loading">
        <div className="webinars-spinner-container">
          <div className="webinars-spinner" />
          <div className="webinars-spinner-inner" />
        </div>
        <p className="webinars-loading-text">Loading premium webinars...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="webinars-error-container">
        <div className="webinars-error-card">
          <div className="error-icon">⚠️</div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <Link to="/" className="webinars-btn webinars-btn-primary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="webinars-page">
      {/* Hero Section */}
      <section className="webinars-hero">
        <div className="webinars-hero-grid" aria-hidden="true" />
        <div className="webinars-hero-content">
          <div className="webinars-hero-badge">
            <span className="badge-dot" />
            Interactive Learning
          </div>
          <h1 className="webinars-hero-title">
            <span>Live</span> Webinars
          </h1>
          <p className="webinars-hero-subtitle">
            Join industry leaders, top engineers, and AI pioneers for interactive, hands-on masterclasses designed to propel your tech career forward.
          </p>
        </div>
      </section>

      {/* Countdown Feature Section */}
      {nextWebinar && timeLeft && (
        <section className="countdown-section">
          <div className="countdown-container">
            <div className="countdown-card">
              <div className="countdown-badge">
                <FaRegHourglass className="countdown-badge-icon" />
                <span>Next Live Session Starts In</span>
              </div>
              <div className="countdown-timer">
                <div className="timer-block">
                  <span className="time-num">{timeLeft.days}</span>
                  <span className="time-label">Days</span>
                </div>
                <span className="timer-colon">:</span>
                <div className="timer-block">
                  <span className="time-num">{timeLeft.hours}</span>
                  <span className="time-label">Hrs</span>
                </div>
                <span className="timer-colon">:</span>
                <div className="timer-block">
                  <span className="time-num">{timeLeft.minutes}</span>
                  <span className="time-label">Mins</span>
                </div>
                <span className="timer-colon">:</span>
                <div className="timer-block">
                  <span className="time-num">{timeLeft.seconds}</span>
                  <span className="time-label">Secs</span>
                </div>
              </div>
              <div className="countdown-details">
                <h3>{nextWebinar.title}</h3>
                <p>{formatDate(nextWebinar.starts_at)} @ {formatTime(nextWebinar.starts_at)}</p>
                <Link to={`/webinars/${nextWebinar.slug}`} className="countdown-register-btn">
                  <span>Secure Your Spot</span>
                  <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Listings and Filters Container */}
      <div className="webinars-main-content">
        <div className="content-inner">
          {/* Grid of Webinars */}
          {filteredWebinars.length === 0 ? (
            <div className="webinars-empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No webinars found</h3>
              <p>Try adjusting your search query or selecting a different tab.</p>
              {searchTerm && (
                <button
                  className="webinars-btn webinars-btn-primary"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <motion.div layout className="webinars-grid">
              <AnimatePresence mode="popLayout">
                {filteredWebinars.map((webinar) => {
                  const status = getWebinarStatus(webinar);
                  return (
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      key={webinar.id}
                      className="webinar-card"
                    >
                      <div className="card-hover-border" />
                      <div className="webinar-card-body">
                        <div className="card-top-row">
                          <span className={`status-badge ${status.class}`}>
                            {status.label}
                          </span>
                          <span className="duration-tag">{webinar.duration_minutes} min</span>
                        </div>

                        <h3 className="webinar-card-title">{webinar.title}</h3>

                        {webinar.description && (
                          <p className="webinar-card-description">{webinar.description}</p>
                        )}

                        <div className="webinar-card-meta">
                          <div className="meta-item">
                            <FaCalendarAlt className="meta-icon" />
                            <span>{formatDate(webinar.starts_at)}</span>
                          </div>
                          <div className="meta-item">
                            <FaClock className="meta-icon" />
                            <span>{formatTime(webinar.starts_at)}</span>
                          </div>
                        </div>

                        <div className="card-footer-action">
                          {status.label === "COMPLETED" ? (
                            <Link
                              to={`/webinars/${webinar.slug}`}
                              className="webinar-card-btn btn-completed"
                            >
                              <span>View Details</span>
                              <FaArrowRight size={14} />
                            </Link>
                          ) : (
                            <Link
                              to={`/webinars/${webinar.slug}`}
                              className="webinar-card-btn"
                            >
                              <span>Register Now</span>
                              <FaArrowRight size={14} />
                            </Link>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
